import queryString from 'query-string';
import { merge } from './reducerUtils';

import {
    SUBSCRIPTION_ACTIONS,
    GENDER_TYPES,
    ALLOWED_SITE_CONTEXT_PATHS,
    scopeTypes,
    BAZAARVOICE_ALLOWED_HEADERS,
    PRODUCT_BOPIS_ORCHESTRATION_ROUTE,
    PRODUCT_QUICKSHOP_ORCHESTRATION_ROUTE
} from '../constants';

import productUtils from '@product/js/utils/utils';

import { generateUserHash } from './hashUtils';

export const CACHE_KEY_GET_FAVORITED_STYLES = 'getFavoritedStyles';
export const CACHE_KEY_GET_HEARTED_PICS = 'getHeartedPics';

export function getFavoritedStylesRoute(state) {
    return {
        errorPrefix: 'ERROR_CORE',
        method: 'GET',
        path: `${state.core.constants.urls.engage}/${state.core.constants.siteId}/me/likes/styles`,
        scope: scopeTypes.AUTHORIZED,
        cacheKey: CACHE_KEY_GET_FAVORITED_STYLES,
        cacheTTL: 60
    };
}

export function getFlatNavigationRoute(state) {
    return {
        errorPrefix: 'ERROR_CORE',
        method: 'GET',
        path: `${state.core.contextPath}orchestration/js/navigation`
    };
}

export function favoriteStyleRoute(state, styleNumber) {
    return {
        errorPrefix: 'ERROR_CORE',
        method: 'POST',
        path: `${state.core.constants.urls.engage}/${state.core.constants.siteId}/me/styles/${styleNumber}/likes`,
        scope: scopeTypes.AUTHORIZED,
        entity: {}
    };
}

export function unfavoriteStyleRoute(state, styleNumber) {
    const path = `${state.core.constants.urls.engage}/${state.core.constants.siteId}/me/styles/${styleNumber}/likes`;

    return {
        errorPrefix: 'ERROR_CORE',
        method: 'delete', // This lower case delete is needed for ie11 unfortunately.
        path: path,
        scope: scopeTypes.AUTHORIZED
    };
}

export function getHeartedPicsRoute(state) {
    return {
        errorPrefix: "ERROR_ENGAGE",
        method: "GET",
        path: `${state.core.contextPath}orchestration/ugc/hearted`,
        scope: scopeTypes.AUTHORIZED,
        cacheKey: CACHE_KEY_GET_HEARTED_PICS,
        cacheTTL: 60
    };
}

export function heartPicRoute(state, id) {
    const base = `${state.core.constants.urls.engage}/${state.core.constants.siteId}`;
    return {
        errorPrefix: "ERROR_ENGAGE",
        method: "POST",
        path: `${base}/me/pictures/${id}/likes`,
        scope: scopeTypes.AUTHORIZED,
        entity: {}
    };
}

export function unheartPicRoute(state, id) {
    const base = `${state.core.constants.urls.engage}/${state.core.constants.siteId}`;

    return {
        errorPrefix: "ERROR_ENGAGE",
        method: "delete",
        path: `${base}/me/pictures/${id}/likes`,
        scope: scopeTypes.AUTHORIZED
    };
}

export function updateAnonymousPassword(state, oldPassword, newPassword, newPasswordConfirm) {
    return {
        errorPrefix: 'ERROR_RESET_PASSWORD',
        method: 'PUT',
        path: `${state.core.constants.urls.profile}/${state.core.constants.siteId}/credentials/password`,
        entity: {
            oldPassword: oldPassword,
            password: newPassword,
            confirmPassword: newPasswordConfirm
        }
    };
}

function baseModifyMySubscriptionRoute(state, action, source, gender = GENDER_TYPES.UNKNOWN) {
    const path = `${state.core.constants.urls.profile}/${state.core.constants.siteId}/me/subscriptions/email`;

    return {
        errorPrefix: 'ERROR_EMAIL_SIGNUP',
        path: path,
        scope: [scopeTypes.AUTHORIZED, scopeTypes.REGISTERED],
        entity: {
            action: action,
            source: source,
            gender: gender
        }
    };
}

function baseModifySubscriptionRoute(state, action, source, gender = GENDER_TYPES.UNKNOWN) {
    const path = `${state.core.constants.urls.profile}/${state.core.constants.siteId}/subscriptions/email`;

    return {
        errorPrefix: 'ERROR_EMAIL_SIGNUP',
        path: path,
        scope: [scopeTypes.GUEST, scopeTypes.AUTHORIZED, scopeTypes.REGISTERED],
        entity: {
            action: action,
            source: source,
            gender: gender
        }
    };
}

export function subscribeToEmailSubscription(state, source, gender = GENDER_TYPES.UNKNOWN) {
    const route = baseModifyMySubscriptionRoute(state, SUBSCRIPTION_ACTIONS.SUBSCRIBE, source, gender);

    route.method = 'POST';
    return route;
}

export function unsubscribeToEmailSubscription(state, source, action, email, gender = GENDER_TYPES.UNKNOWN) {
    const path = `${state.core.constants.urls.profile}/${state.core.constants.siteId}/subscriptions/email`;

    return {
        errorPrefix: 'ERROR_UNSUBSCRIBE',
        path: path,
        method: 'POST',
        entity: {
            action: action,
            source: source,
            gender: gender,
            emailAddress: email
        }
    };
}

export function subscribeToAnonymousEmailSubscription(state, source, emailAddress, gender = GENDER_TYPES.UNKNOWN,
    extraRequestData = {}) {
    let requestBody = extraRequestData || {};

    requestBody = Object.assign({}, requestBody, {
        emailAddress: emailAddress
    });

    const route = baseModifySubscriptionRoute(state, SUBSCRIPTION_ACTIONS.SUBSCRIBE, source, gender);

    route.method = 'POST';
    route.entity = Object.assign({}, route.entity, requestBody);

    return route;
}

/**
 * Prepares and makes thes the call to `makeRequest` for categories
 * @param  {object} state         Current state of the redux store
 * @param  {string} categoryId    The ID of the category to request
 * @param  {string} categoryContext search, brand, or category type
 * @param  {string} searchTerm    The keyword to search on
 * @param  {number} requestedPage Page number to request
 * @param  {object} aQueryString   Request query params, as string
 * @return {object}               Returns the response of `makeRequest`
 */
export function getCategoryOrchestration(state, categoryId, categoryContext, searchTerm, requestedPage, aQueryString) {
    const qs = queryStringHelper(aQueryString);

    let path = `${state.core.contextPath}orchestration/features/${categoryContext}/`;

    switch (categoryContext) {
        case 'search':
            path = path + qs;
            break;
        case 'brands':
        case 'category':
        default:
            path = path + `${categoryId}${qs}`;
            break;
    }

    const route = {
        errorPrefix: 'ERROR_CATEGORY',
        method: 'GET',
        path: path,
        localStorage: false,
        reduxCache: false
    };

    return route;
}

export function getCoreOrchestration(state, feature = 'core') {
    const qs = queryStringHelper('', 'core');

    /**
     * Need to get context path straight from URL here
     * since we don't have access to the full core yet.
     */

    const contextPath = location.pathname.split('/')[1];
    const contextAvailable = _.findIndex(ALLOWED_SITE_CONTEXT_PATHS, (o) => {
        return o === contextPath;
    });
    const currentContext = contextAvailable !== -1 ? `/${contextPath}/` : '/';

    const route = {
        errorPrefix: 'ERROR_CORE',
        method: 'GET',
        path: `${currentContext}orchestration/features/${feature}${qs}`,
        localStorage: true,
        reduxCache: false
    };

    return route;
}

/**
 * Get a product for a given product slug
 * @param {Object} state    Redux state
 * @param {Object|String} queryParams   Hash of query params OR stringified queryparams (no ?)
 * @param {Object|String} routeSlug orchestration route slug (defaults to quickshop)
 * @returns {Object} product orchestration route
 */
export function getProduct(state, queryParams = {}, routeSlug = PRODUCT_QUICKSHOP_ORCHESTRATION_ROUTE) {
    let queryStr;

    if (_.isString(queryParams)) {
        queryStr = `?${queryParams}`;
    } else {
        const inventoryFeedCountry = _.get(state, 'core.inventoryFeedCountry');

        if (inventoryFeedCountry) {
            queryParams.inventoryCountry = inventoryFeedCountry;
        }

        queryStr = Object.keys(queryParams).length ? `?${queryString.stringify(queryParams)}` : '';
    }

    const route = {
        errorPrefix: 'ERROR_PRODUCT',
        method: 'GET',
        path: `${state.core.contextPath}orchestration/features/shop/${routeSlug}/${queryStr}`,
        reduxCache: true,
        localStorage: false,
        cacheTTL: 5000
    };

    return route;
}

export function getBopisPoolRoute(state, queryParams = {}) {
    return getProduct(state, queryParams, PRODUCT_BOPIS_ORCHESTRATION_ROUTE);
}

export function getQuickshopRoute(state, queryParams = {}) {
    return getProduct(state, queryParams, PRODUCT_QUICKSHOP_ORCHESTRATION_ROUTE);
}

export function getGiftCardBalance(state) {
    const path = `${state.core.constants.urls.checkout}/${state.core.constants.siteId}/balance-inquiries`;

    const route = {
        errorPrefix: 'ERROR_HELP',
        method: 'POST',
        path: path,
        entity: {
            'paymentType': 'GIFT_CARD',
            'giftCard': {
                'cardNumber': `${state.help.giftCard.cardNumber}`,
                'pinRequired': true,
                'pin': `${state.help.giftCard.pin}`
            }
        },
        headers: {
            'X-Urbn-Captcha-Token': state.help.giftCard.recaptchaToken
        }
    };

    return route;
}

export function removeWishlistItemRoute(state, itemKey) {
    const path = `${state.core.constants.urls.checkout}/${state.core.constants.siteId}/carts/items/${itemKey}`;
    const route = {
        errorPrefix: 'ERROR_WISHLIST',
        method: 'delete', // This lower case delete is needed for ie11 unfortunately.
        path: path
    };

    return route;
}

/*
 * Checkout Cart & Wishlist share the same update route
 */
export function updateCartItemRoute(state, entity, itemKey) {
    const checkoutSlug = state.core.constants.urls.checkout;
    const siteId = state.core.constants.siteId;

    return {
        errorPrefix: 'ERROR_PRODUCT',
        entity: entity,
        method: 'PUT',
        path: `${checkoutSlug}/${siteId}/carts/items/${itemKey}`
    };
}

/*
 * Moves a cart item to target cart type
 */
export function moveCartItemRoute(state, cartItemId, type) {
    const checkoutSlug = state.core.constants.urls.checkout;
    const siteId = state.core.constants.siteId;

    return {
        entity: {},
        errorPrefix: 'ERROR_PRODUCT',
        method: 'PUT',
        path: `${checkoutSlug}/${siteId}/carts/items/${cartItemId}/types/${type}`
    };
}

export function getWishlistsRoute(state, params) {
    return {
        method: 'GET',
        path: `/api/checkout/v0/${state.core.siteId}/wishlists`,
        userAction: 'get-wishlists',
        scope: 'AUTHORIZED'
    };
}

export function addToWishlistRoute(state, params, defaultWishlist) {
    const route = {
        errorPrefix: 'ERROR_WISHLIST',
        method: 'POST',
        path: `/api/checkout/v0/${state.core.siteId}/wishlists/${defaultWishlist}/items`,
        entity: params,
        userAction: 'add-to-wishlist',
        scope: scopeTypes.AUTHORIZED
    };

    return route;
}

export function moveToWishlistRoute(state, params, lineItemId) {
    const route = {
        errorPrefix: 'ERROR_WISHLIST',
        method: 'PUT',
        path: `/api/checkout/v0/${state.core.siteId}/carts/items/${lineItemId}/types/wishlist`,
        entity: params,
        userAction: 'move-to-wishlist',
        scope: scopeTypes.AUTHORIZED
    };

    return route;
}

export function getWishlistItemsRoute(state, wishlistId, page) {
    const route = {
        errorPrefix: 'ERROR_WISHLIST',
        method: 'GET',
        path: `${state.core.contextPath}orchestration/features/wishlist/${wishlistId}/?page=${page}`,
        userAction: 'get-wishlist-items',
        scope: scopeTypes.AUTHORIZED
    };

    return route;
}

export function getCartRoute(state) {
    const now = Date.now();
    const basePath = `${state.core.constants.urls.checkout}/${state.core.constants.siteId}`;
    const path = `${basePath}/minicart?${now}`;

    const route = {
        errorPrefix: 'ERROR_PRODUCT',
        method: 'GET',
        path: path
    };

    return route;
}

/*
 * Search for a wishlist
 * @param {Object} params - Search params
 * @param {String} params.email - Search params
 * @param {String} params.firstName - first name of wishlist owner
 * @param {String} params.lastName - last name of wishlist owner
 */
export function searchWishlistRoute(state, params) {
    const queryStr = queryString.stringify(params);

    const route = {
        errorPrefix: 'ERROR_WISHLIST',
        method: 'GET',
        path: `${state.core.contextPath}orchestration/features/wishlist/search?${queryStr}`
    };

    return route;
}

export function addToCartRoute(state, params) {
    const route = {
        errorPrefix: 'ERROR_PRODUCT',
        method: 'POST',
        path: `/api/checkout/v0/${state.core.siteId}/carts/items`,
        entity: params,
    };

    return route;
}

export function getBVLocale(state, locale) {
    const localeUK = state.core.constants.thirdParty.default.bazaarvoice.uk_locale;
    const localeUS = state.core.constants.thirdParty.default.bazaarvoice.us_locale;

    // Using _.get since FP is the only brand that has China
    const localeCN = _.get(state.core.constants.thirdParty.default.bazaarvoice, 'cn_locale', "");

    switch (locale) {
        case 'de_DE':
            return localeUK;
        case 'en_GB':
            return localeUK;
        case 'fr_FR':
            return localeUK;
        case 'en_US':
            return localeUS;
        case 'fr_CA':
            return localeUS;
        case 'en_CA':
            return localeUS;
        case 'zh_CN':
            return localeCN;
        default:
            return localeUS;
    }
}

export function reviewPagesRoute(state, limit, offset) {
    const product = productUtils.getTargetedProduct(state);
    const bvApiPath = state.core.constants.thirdParty.default.bazaarvoice.get_path;
    const bvApiKey = product.bvReviewKey || _.get(state, 'mto.bvReviewKey', null);
    const reviewsForLocale = _.get(
        state,
        `core.constants.reviewsByLocale`,
        {}
    );
    const productId = product.styleNumber;
    const bvApiEndpoint = state.core.constants.thirdParty.default.bazaarvoice.read_reviews_endpoint;
    const bvApiVersion = state.core.constants.thirdParty.default.bazaarvoice.version;
    const reviewCodeForLocale = _.get(reviewsForLocale, state.core.language, "");

    const requestUrl = `${bvApiPath}${bvApiEndpoint}?apiversion=${bvApiVersion}&passkey=${bvApiKey}&Filter=ProductId:${productId}&Include=Products,Comments,Authors&Stats=Reviews&Limit=${limit}&Offset=${offset}&Locale=${reviewCodeForLocale}`; // eslint-disable-line max-len
    const route = {
        method: 'GET',
        path: requestUrl,
        reduxCache: true,
        cacheTTL: 300,
        allowedHeaders: BAZAARVOICE_ALLOWED_HEADERS
    };

    return route;
}

export function orchestrationDeleteWishlistRoute(state, wishlistId) {
    const route = {
        errorPrefix: 'ERROR_WISHLIST',
        method: 'delete', // This lower case delete is needed for ie11 unfortunately.
        path: `${state.core.contextPath}orchestration/features/wishlist/${wishlistId}`,
        userAction: 'delete-wishlist',
        scope: scopeTypes.AUTHORIZED
    };

    return route;
}

export function orchestrationWishlistsPutRoute(state, wishlistId, params) {
    const route = {
        errorPrefix: 'ERROR_WISHLIST',
        method: 'PUT',
        path: `${state.core.contextPath}orchestration/features/wishlist/${wishlistId}`,
        entity: params,
        scope: scopeTypes.AUTHORIZED
    };

    return route;
}

export function orchestrationWishlists(state) {
    const route = {
        errorPrefix: 'ERROR_WISHLIST',
        method: 'GET',
        path: `${state.core.contextPath}orchestration/features/wishlist`
    };

    return route;
}

export function orchestrationPromoBanners(state, promoType) {
    const uri = escape(window.location.pathname + window.location.search);
    const route = {
        errorPrefix: 'ERROR_PROMO_BANNER',
        method: 'GET',
        path: `${state.core.contextPath}orchestration/promo_banner/${promoType}?uri=${uri}`
    };

    return route;
}

export function reviewFieldsRoute(state) {
    const product = productUtils.getTargetedProduct(state);
    const bvApiPath = state.core.constants.thirdParty.default.bazaarvoice.get_path;
    const bvApiKey = product.bvReviewKey || _.get(state, 'mto.bvReviewKey', null);
    const productId = product.styleNumber;
    const locale = state.core.language.replace('-', '_');
    const bvApiEndpoint = state.core.constants.thirdParty.default.bazaarvoice.submit_reviews_endpoint;
    const bvApiVersion = state.core.constants.thirdParty.default.bazaarvoice.version;
    const bvLocale = getBVLocale(state, locale);
    const requestUrl = `${bvApiPath}${bvApiEndpoint}?apiversion=${bvApiVersion}&passkey=${bvApiKey}&Locale=${bvLocale}&productid=${productId}&action=`; // eslint-disable-line max-len
    // disabling reduxCache & localStorage for duplicate submission functionally to work
    const route = {
        method: 'GET',
        path: requestUrl,
        reduxCache: false,
        localStorage: false,
        allowedHeaders: BAZAARVOICE_ALLOWED_HEADERS
    };

    return route;
}

export function getProfile(state, suppressLogin = false, includeLoyalty = false, includePrivacy = false) {
    const profileUrl = _.get(state, 'core.constants.urls.profile', '');
    const siteId = _.get(state, 'core.constants.siteId', '');

    const route = {
        errorPrefix: 'ERROR_CORE',
        method: 'GET',
        path: `${profileUrl}/${siteId}/me?loyalty=${includeLoyalty}&privacyInfo=${includePrivacy}`,
        suppressLoginPrompt: suppressLogin
    };

    return route;
}

export function createWishlist(state, params) {
    const route = {
        errorPrefix: 'ERROR_WISHLIST',
        method: 'POST',
        path: `${state.core.constants.urls.wishlist}/${state.core.constants.siteId}/wishlists`,
        entity: params,
        userAction: 'create-wishlist',
        scope: scopeTypes.AUTHORIZED
    };

    return route;
}

export function getProductDetailRoute(state, poolId,
    { slug, productIds, styleNumbers, excludeFilter, includePools, trim } = {}) {
    let path = `${state.core.constants.urls.catalog}/${state.core.constants.siteId}/pools/${poolId}/products?`;

    /* eslint-disable angular/typecheck-array */
    function generateQueryString(aSlug, theProductIds, theStyleNumbers, shouldExcludeFilter, shouldIncludePools,
        shouldTrim) {
        const parts = [];

        if (aSlug) {
            parts.push(`slug=${aSlug}`);
        } else if (theProductIds) {
            if (Array.isArray(theProductIds) && theProductIds.length > 0) {
                parts.push(`product-id=${theProductIds.join(',')}`);
            } else {
                parts.push(`product-id=${theProductIds}`);
            }
        } else if (theStyleNumbers) {
            if (Array.isArray(theStyleNumbers) && theStyleNumbers.length > 0) {
                parts.push(`style-number=${theStyleNumbers.join(',')}`);
            } else {
                parts.push(`style-number=${theStyleNumbers}`);
            }
        }

        if (shouldExcludeFilter) {
            parts.push(`exclude-filter=${shouldExcludeFilter}`);
        }

        if (shouldIncludePools) {
            if (Array.isArray(shouldIncludePools) && shouldIncludePools.length > 0) {
                parts.push(`include-pools=${shouldIncludePools.join(',')}`);
            } else {
                parts.push(`include-pools=${shouldIncludePools}`);
            }
        }

        if (shouldTrim) {
            parts.push('trim=true');
        }

        return parts.join('&');
    }
    /* eslint-enable angular/typcheck-array */

    const theQueryString = generateQueryString(slug, productIds, styleNumbers, excludeFilter, includePools, trim);

    path = path.concat(theQueryString);

    const route = {
        errorPrefix: 'ERROR_PRODUCT',
        method: 'GET',
        path: path
    };

    return route;
}

export function getProductsRoute(
    state,
    browseCategory = '',
    productArray = [],
    queryParam = 'styleNumbers',
    slimProductModel = false,
    excludeOOS = false
) {
    let queryStr = '';

    if (!_.isEmpty(productArray)) {
        queryStr = `${queryStr}${queryParam}=${productArray.join(',')}&exclude-filter=oos-color`;
    }

    if (browseCategory) {
        queryStr = `${queryStr}&category=${browseCategory}`;
    }

    const inventoryFeedCountry = _.get(state, 'core.inventoryFeedCountry');

    if (inventoryFeedCountry) {
        queryStr = `${queryStr}&inventoryCountry=${inventoryFeedCountry}`;
    }

    if (slimProductModel) {
        queryStr = `${queryStr}&slimProductModel=true`;
    }

    if (excludeOOS) {
        queryStr = `${queryStr}&filter-oos=true`;
    }

    const route = {
        errorPrefix: 'ERROR_PRODUCT',
        method: 'GET',
        path: `${state.core.contextPath}orchestration/features/shop/multiple/?${queryStr}`,
        reduxCache: true,
        localStorage: false,
        cacheTTL: 5000
    };

    return route;
}

export function submitReviewRoute(state, formData) {
    const product = productUtils.getTargetedProduct(state);
    const bvApiPath = state.core.constants.thirdParty.default.bazaarvoice.post_path;
    const bvApiKey = product.bvReviewKey || _.get(state, 'mto.bvReviewKey', null);
    const productId = product.styleNumber;
    const locale = state.core.language.replace('-', '_');
    const bvApiEndpoint = state.core.constants.thirdParty.default.bazaarvoice.submit_reviews_endpoint;
    const bvApiVersion = state.core.constants.thirdParty.default.bazaarvoice.version;
    const bvLocale = getBVLocale(state, locale);
    const userName = _.get(formData, 'usernickname');
    const userEmail = _.get(formData, 'useremail', '');

    let userFieldName = 'userid';
    let userId = _.get(state, 'core.profile.profileId', userEmail);

    if (_.get(state, `core.constants.pdpOptions.use_hash_for_reviews`)) {
        const newUserName = userName !== state.core.profile.loyaltyUserName ? userName : null;

        userFieldName = 'user';
        userId = generateUserHash(_.get(state, 'core.constants.pdpOptions'), _.get(state, `core.profile`), newUserName, userEmail);
    }

    const requestUrl = `${bvApiPath}${bvApiEndpoint}?apiversion=${bvApiVersion}&passkey=${bvApiKey}&Locale=${bvLocale}&productid=${productId}&${userFieldName}=${userId}&action=Submit`; // eslint-disable-line max-len

    // Turn on email notifications for BV
    // See https://developer.bazaarvoice.com/conversations-api/tutorials/submission/input-types
    const newFormData = Object.assign({}, formData, { sendemailalertwhencommented: true });

    const route = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        },
        method: 'POST',
        entity: newFormData,
        path: requestUrl
    };

    return route;
}

/**
 * Smashes together the request query string with the URL query string, if they
 * happen to be different, to ensure that the `chrome` state gets passed along
 * to API orchestration calls
 * @param  {object/string} qs   The API request params, as an object or a string
 * @param  {string} feature     URBNWeb feature name
 * @return {string}    the rebuilt query string, with `chrome` value, if present
 */
export function queryStringHelper(qs = '', feature = '') {
    let pageQueryString;

    if (queryString.stringify(queryString.parse(window.location.search)).includes('webview')) {
        pageQueryString = queryString.parse(window.location.search);
    } else {
        return `?${qs}`;
    }
    let requestQueryString = {};
    const builtQueryString = {};

    if (_.isString(qs)) {
        requestQueryString = queryString.parse(qs);
    }

    if (Object.keys(pageQueryString).length > 0) {
        let tempQueryString = merge(pageQueryString, requestQueryString);

        if (feature === 'core') {
            for (const param in tempQueryString) {
                if (param === 'webview') {
                    tempQueryString = {
                        webview: tempQueryString[param]
                    };

                    return `?${queryString.stringify(tempQueryString)}`;
                }
            }

            return '';
        }

        // strip out webview=true, we don't need it anywhere aside from core
        for (const param in tempQueryString) {
            if (param !== 'webview') {
                builtQueryString[param] = tempQueryString[param];
            }
        }

        return `?${queryString.stringify(builtQueryString)}`;
    } else if (qs.length > 0) {
        return `?${qs}`;
    }

    return '';
}

// TODO: Going forward, we will need to add categoryId and navigationItemId as params
export function richRelevanceRoute(rrApiPath, rrEndpoint, rrClientKey, rrApiKey, rrUserId,
    rrSessionId, rrPlacementIds, productId, categoryId, udd) {
    function buildPlacementsValue(placements) {
        let placementsList;

        if (_.isArray(placements)) {
            placementsList = _.cloneDeep(placements);
        } else if (_.isString(placements) && _.includes(placements, ',')) {
            placementsList = placements.split(',');
        } else {
            placementsList = [placements];
        }

        return placementsList.join('|');
    }

    const apiQueryParams = {
        apiClientKey: rrClientKey,
        apiKey: rrApiKey,
        userId: rrUserId,
        sessionId: rrSessionId,
        placements: buildPlacementsValue(rrPlacementIds),
        productId: productId,
        categoryId: categoryId,
        includeStrategyData: 'true',
        returnMinimalRecItemData: 'true',
        udd: udd
    };

    // Don't append these if they don't exist
    if (productId) {
        apiQueryParams.productId = productId;
    }

    if (categoryId) {
        apiQueryParams.categoryId = categoryId;
    }

    const apiQueryString = queryString.stringify(apiQueryParams);

    const route = {
        errorPrefix: 'ERROR_RR',
        method: 'GET',
        path: `${rrApiPath}${rrEndpoint}?${apiQueryString}`
    };

    return route;
}

export function getLoyaltySubscription(state, suppressLogin = false) {
    const path = `${state.core.constants.urls.profile}/${state.core.constants.siteId}/me/subscriptions/loyalty`;
    const route = {
        errorPrefix: 'ERROR_CORE',
        method: 'GET',
        path: path,
        suppressLoginPrompt: suppressLogin
    };

    return route;
}

export function sendLoyaltyEvents(state, entity) {
    const path = `${state.core.constants.urls.profile_v1}/${state.core.constants.siteId}/me/loyalty/events`;
    const route = {
        errorPrefix: 'ERROR_CORE',
        method: 'POST',
        path,
        entity
    };

    return route;
}

export function getProfileAddresses(state, suppressLogin = false) {
    const path = `${state.core.constants.urls.profile}/${state.core.constants.siteId}/me/addresses`;
    const route = {
        errorPrefix: 'ERROR_CORE',
        method: 'GET',
        path: path,
        suppressLoginPrompt: suppressLogin
    };

    return route;
}

export function getProfilePayments(state, suppressLogin = false) {
    const path = `${state.core.constants.urls.profile}/${state.core.constants.siteId}/me/payments`;
    const route = {
        errorPrefix: 'ERROR_CORE',
        method: 'GET',
        path: path,
        suppressLoginPrompt: suppressLogin
    };

    return route;
}

export function createLoyaltySubscription(state) {
    const path = `${state.core.constants.urls.profile}/${state.core.constants.siteId}/me/subscriptions/loyalty`;
    const route = {
        errorPrefix: 'ERROR_CORE',
        method: 'POST',
        path: path,
        entity: {
            'channelId': 'WEB',
            'loyaltyDate': 0,
            'channel': 'WEB'
        },
    };

    return route;
}

export function addProfileAddress(state, entity) {
    const path = `${state.core.constants.urls.profile}/${state.core.constants.siteId}/me/addresses`;
    const route = {
        errorPrefix: 'ERROR_CORE',
        method: 'POST',
        path: path,
        entity: entity,
    };

    return route;
}

export function updateProfile(state, entity) {
    const path = `${state.core.constants.urls.profile}/${state.core.constants.siteId}/me`;
    const route = {
        errorPrefix: 'ERROR_CORE',
        method: 'PUT',
        path: path,
        entity: entity,
    };

    return route;
}

export function updateProfileWebPreferences(state, entity) {
    const path = `${state.core.constants.urls.profile}/${state.core.constants.siteId}/me/preferences/web`;
    const route = {
        errorPrefix: 'ERROR_CORE',
        method: 'PUT',
        path: path,
        entity: entity,
    };

    return route;
}

export function createProfilePreferences(state, entity = {}) {
    const requestBody = {
        global: {
            language: _.get(state, 'core.language', ''),
            country: _.get(state, 'core.userLocation.country_code', ''),
            currency: _.get(state, 'core.currency', '')
        }
    };
    _.merge(requestBody, entity);

    const path = `${state.core.constants.urls.profile}/${state.core.constants.siteId}/me/preferences`;
    const route = {
        errorPrefix: 'ERROR_CORE',
        method: 'POST',
        path: path,
        entity: requestBody,
    };

    return route;
}

export function updateProfileGlobalPreferences(state, entity) {
    const path = `${state.core.constants.urls.profile}/${state.core.constants.siteId}/me/preferences/global`;
    const route = {
        errorPrefix: 'ERROR_CORE',
        method: 'PUT',
        path: path,
        entity: entity,
    };

    return route;
}

export function patchProfileGlobalPreferences(state, entity) {
    const path = `${state.core.constants.urls.profile_v1}/${state.core.constants.siteId}/me/preferences`;
    const route = {
        errorPrefix: 'ERROR_CORE',
        method: 'PATCH',
        path: path,
        entity: entity,
    };

    return route;
}

export function getProfileWebPreferences(state) {
    const path = `${state.core.constants.urls.profile}/${state.core.constants.siteId}/me/preferences/web`;
    const route = {
        errorPrefix: 'ERROR_CORE',
        method: 'GET',
        path: path,
    };

    return route;
}

export function getProfilePreferences(state) {
    const path = `${state.core.constants.urls.profile}/${state.core.constants.siteId}/me/preferences/global`;
    const route = {
        errorPrefix: 'ERROR_CORE',
        method: 'GET',
        path: path,
    };

    return route;
}

export function getFavoriteStores(state, queryParam) {
    const mislconfig = state.core.constants.mislConfig;
    const url = state.core.constants.urls.misl;
    const mislKey = mislconfig.misl_urbn_key;
    const path = `${url}/stores/search?urbn_key=${mislKey}&brandId=${mislconfig.brandId}&storeNumber=${queryParam}`;
    const route = {
        errorPrefix: 'ERROR_CORE',
        method: 'GET',
        path: path,
    };

    return route;
}

export function sendMarketplaceMessageRequest(state, formData) {
    return {
        errorPrefix: 'ERROR_REQUEST_MARKETPLACE_MESSAGE',
        method: 'POST',
        path: `${state.core.constants.urls.profile}/${state.core.constants.siteId}/messages`,
        entity: formData
    };
}

/**
 * Builds up generic email service route
 * @param  {String} brandCode         Number code of current brand
 * @param  {String} [campaignId=null] EMS campaign ID for Experian email provider
 * @return {String}                   The route to the correct email provider endpoint
 */
export function getManualEmailRoute(brandCode, campaignId = null) {
    const baseRoute = '/orchestration/generic_email';
    const brandRoute = brandCode ? `${baseRoute}?brand=${brandCode}` : baseRoute;
    const emailRoute = campaignId ? `${brandRoute}&campaignId=${campaignId}` : brandRoute;

    return emailRoute;
}

export function getContactUsRoute(state, entity) {
    const path = `${state.core.constants.urls.profile}/${state.core.constants.siteId}/contact-us`;
    const route = {
        method: 'POST',
        path: path,
        entity: entity,
    };

    return route;
}

export function getEmailSignupRoute(state) {
    const path = `${state.core.constants.urls.profile}/${state.core.constants.siteId}/subscriptions/email`;

    return {
        errorPrefix: 'ERROR_EMAIL_SIGNUP',
        path: path,
        entity: {
            emailAddress: `${state.core.modal.emailSignup.email}`
        }
    };
}

export function getProfileAvailability(state, username) {
    const path = `${state.core.constants.urls.profile}/${state.core.constants.siteId}/profiles/availability?username=${username}`; // eslint-disable-line max-len
    const route = {
        errorPrefix: 'ERROR_CORE',
        method: 'GET',
        path: path
    };

    return route;
}

export function getStoresForPersonalShopper(state) {
    const shopperType = _.get(state, 'core.genericEmailService.appointmentType', 'personalShopper');
    const path = `${state.core.constants.urls.misl}/stores/search?urbn_key=${state.core.constants.mislConfig.misl_urbn_key}&brandId=${state.core.constants.mislConfig.brandId}&anthropologie.${shopperType}=true`; // eslint-disable-line max-len
    const route = {
        method: 'GET',
        path: path
    };

    return route;
}

export function submitCatalogRequest(state, formData) {
    return {
        errorPrefix: 'ERROR_REQUEST_CATALOG',
        method: 'POST',
        path: `${state.core.constants.urls.profile}/${state.core.constants.siteId}/catalog-requests`,
        entity: formData
    };
}

/**
 * Get UGC Posts from Dash or Engage
 *
 * @param  {object} state The current state of the redux store
 * @param  {object} config UGC query config
 * @returns {Object} route object
 */
export function getUgcPostsRoute(state, config) {
    const qs = queryString.stringify(config);
    const path = `${state.core.contextPath}orchestration/ugc?${qs}`;
    const route = {
        errorPrefix: 'ERROR_UGC',
        method: 'GET',
        path: path
    };

    return route;
}

/**
 * Get Groundhog product recommendations
 *
 * @param {Object} state Redux state
 * @param {String} styleNumber Like '4130024095656' not 'AN-4130024095656-000'
 * @param {Number} limit Maximum number of recommendations to return
 * @returns {Object} Route object
 */
export function getGroundhogRecommendations(state, styleNumber, limit = 6) {
    const queryParams = {
        limit: limit,
        productId: styleNumber
    };

    const siteId = state.core.constants.siteId;
    const poolId = state.core.inventoryPool;
    const qs = queryString.stringify(queryParams);
    const path = `/api/recommendation/v0/${siteId}/pools/${poolId}/?${qs}`;
    const route = {
        errorPrefix: 'ERROR_RECOMMENDATIONS',
        method: 'GET',
        path: path
    };

    return route;
}

/**
 * Route used to post a clickstream event to groundhog
 *
 * @param {Object} state Redux state
 * @param {Object} body Body of the clickstream event. Varies. But should contain pageId and page
 * @returns {Object} route object
 */
export function getGroundhogClicksteamEventRoute(state, body) {
    const siteId = state.core.constants.siteId;
    const path = `/api/clickstream/v0/${siteId}`;

    return {
        errorPrefix: 'ERROR_CLICKSTREAM',
        method: 'POST',
        entity: body,
        path: path
    };
}

/**
 * Route to get country-specific site configuration data
 *
 * @param {Object} state       redux state
 * @param {String} countryCode country whose information you want
 * @returns {Object} Route object
 */
export function getCountryConfiguration(state, countryCode) {
    const siteId = _.get(state, 'core.constants.siteId');
    const path = `${state.core.constants.urls.catalog}/${siteId}/configuration/countries/${countryCode}`;

    const route = {
        method: 'GET',
        path: path
    };

    return route;
}

function saytOrchestration(state, searchTerm) {
    const path = `${state.core.contextPath}orchestration/features/sayt`;
    const queryParams = (searchTerm) ? { q: searchTerm } : {};

    const route = {
        method: 'GET',
        path: path,
        params: queryParams,
        cacheTTL: 0
    };

    return route;
}

export function getSizeGuideDataRoute(state, params) {
    const qs = queryString.stringify(params);

    return {
        errorPrefix: "ERROR_SIZEGUIDE",
        method: "GET",
        path: `${state.core.contextPath}orchestration/features/shop/sizeguide?${qs}`,
        reduxCache: true,
        cacheTTL: 300
    };
}

export function getContentPageFromSlug(state, params) {
    const qs = queryString.stringify(params);
    return {
        errorPrefix: "ERROR_CONTENT_PAGE",
        method: "GET",
        path: `${state.core.contextPath}orchestration/contentPages/?${qs}`,
        reduxCache: true,
        cacheTTL: 300
    };
}

export function getRewardsRoute(state) {
    const userId = _.get(state, 'rewards.loyalty_user_id') || _.get(state, 'core.profile.loyalty.loyaltyId');
    const userQueryString = (userId) ? `userId=${userId}` : '';

    const route = {
        errorPrefix: 'ERROR_REWARDS',
        method: 'GET',
        path: `${state.core.contextPath}orchestration/features/rewards?${userQueryString}`,
        userAction: 'get-rewards',
    };

    return route;
}

export function enterGiveawayRoute(state, giveawayId, body) {
    const path = `${state.core.constants.urls.profile_v1}/${state.core.constants.siteId}/me/loyalty/campaigns/${giveawayId}/events`;
    const route = {
        errorPrefix: 'ENTER_GIVEAWAY',
        method: 'POST',
        path: path,
        entity: body,
    };

    return route;
}

export function cancelOrderRoute(state, isAuth, orderNumber, body, token) {
    const authenticatedUrlModifer = isAuth ? 'me/' : '';
    const profileEndpoint = state.core.constants.urls.profile;
    const { siteId } = state.core.constants;
    const path = `${profileEndpoint}/${siteId}/${authenticatedUrlModifer}orders/${orderNumber}/cancellation`;
    const headers = !isAuth ? { 'X-Urbn-Captcha-Token': token } : {};

    const route = {
        method: 'POST',
        path: path,
        entity: body,
        headers: headers
    };

    return route;
}

export default {
    addProfileAddress: addProfileAddress,
    addToCartRoute: addToCartRoute,
    createAnonymousEmailSubscription: subscribeToAnonymousEmailSubscription,
    createEmailSubscription: subscribeToEmailSubscription,
    createLoyaltySubscription: createLoyaltySubscription,
    favoriteStyle: favoriteStyleRoute,
    getBVLocale: getBVLocale,
    getContactUsRoute: getContactUsRoute,
    getCountryConfiguration: getCountryConfiguration,
    getEmailSignupRoute: getEmailSignupRoute,
    getFavoritedStyles: getFavoritedStylesRoute,
    getFavoriteStores: getFavoriteStores,
    getGiftCardBalance: getGiftCardBalance,
    getGroundhogClicksteamEventRoute: getGroundhogClicksteamEventRoute,
    getGroundhogRecommendations: getGroundhogRecommendations,
    getLoyaltySubscription: getLoyaltySubscription,
    sendLoyaltyEvents: sendLoyaltyEvents,
    getManualEmailRoute: getManualEmailRoute,
    getProductDetail: getProductDetailRoute,
    getProductsRoute: getProductsRoute,
    getProfile: getProfile,
    getProfileAddresses: getProfileAddresses,
    getProfileAvailability: getProfileAvailability,
    getProfilePayments: getProfilePayments,
    createProfilePreferences: createProfilePreferences,
    getProfilePreferences: getProfilePreferences,
    getProfileWebPreferences: getProfileWebPreferences,
    getStoresForPersonalShopper: getStoresForPersonalShopper,
    orchestrationPromoBanners: orchestrationPromoBanners,
    reviewFieldsRoute: reviewFieldsRoute,
    reviewPagesRoute: reviewPagesRoute,
    saytOrchestration: saytOrchestration,
    submitCatalogRequest: submitCatalogRequest,
    submitReviewRoute: submitReviewRoute,
    unfavoriteStyle: unfavoriteStyleRoute,
    unsubscribeToEmailSubscription: unsubscribeToEmailSubscription,
    updateProfile: updateProfile,
    updateProfileWebPreferences: updateProfileWebPreferences,
    updateResetPassword: updateAnonymousPassword,
    sendMarketplaceMessageRequest: sendMarketplaceMessageRequest,
    getRewardsRoute: getRewardsRoute,
    enterGiveawayRoute: enterGiveawayRoute,
    cancelOrderRoute: cancelOrderRoute
};
